import constate from "constate";
import { useReducer, useState } from "react";
const initialState = {
  queuedColumnSelections: [],
  columnSelections: [],
  isSelectingColumns: false,
  selectedColumnsState: void 0
};
const reducer = (state, action) => {
  switch (action.type) {
    case "SET_COLUMN_SELECTIONS": {
      const newSelections = new Set(action.payload);
      const currentSelections = new Set(state.columnSelections);
      const hasSameSelections = newSelections.size === currentSelections.size && [...newSelections].every((v) => currentSelections.has(v));
      if (hasSameSelections)
        return state;
      return { ...state, columnSelections: action.payload };
    }
    case "SET_QUEUED_COLUMN_SELECTIONS":
      return { ...state, queuedColumnSelections: action.payload };
    case "CLEAR_COLUMN_SELECTIONS":
      if (state.columnSelections.length === 0)
        return state;
      return { ...state, columnSelections: [] };
    case "APPLY_QUEUED_COLUMN_SELECTIONS":
      return {
        ...state,
        columnSelections: [
          .../* @__PURE__ */ new Set([
            ...state.columnSelections,
            ...state.queuedColumnSelections
          ])
        ],
        queuedColumnSelections: []
      };
    case "SET_IS_SELECTING_COLUMNS":
      return { ...state, isSelectingColumns: action.payload };
    case "SET_SELECTED_COLUMNS_STATE":
      return { ...state, selectedColumnsState: action.payload };
    default:
      return state;
  }
};
const useColumnSelection = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [focusedColumn, setFocusedColumn] = useState(null);
  const {
    columnSelections,
    queuedColumnSelections,
    isSelectingColumns,
    selectedColumnsState
  } = state;
  const _addUniqueColumnSelections = (values) => {
    const noDupes = [.../* @__PURE__ */ new Set([...columnSelections, ...values])];
    dispatch({ type: "SET_COLUMN_SELECTIONS", payload: noDupes });
  };
  const setUniqueColumnSelections = (values) => {
    const noDupes = [...new Set(values)];
    dispatch({ type: "SET_COLUMN_SELECTIONS", payload: noDupes });
  };
  const addQueuedColumnSelections = (values) => {
    const singleValueExists = values.length === 1 && columnSelections.includes(values[0]);
    if (singleValueExists && queuedColumnSelections.length === 0) {
      const newState = columnSelections.filter((i) => i !== values[0]);
      dispatch({ type: "SET_COLUMN_SELECTIONS", payload: newState });
    } else {
      dispatch({ type: "SET_QUEUED_COLUMN_SELECTIONS", payload: [...values] });
    }
  };
  const clearColumnSelections = () => {
    dispatch({ type: "CLEAR_COLUMN_SELECTIONS" });
  };
  const applyQueuedColumnSelections = () => {
    if (queuedColumnSelections.length === 0)
      return;
    _addUniqueColumnSelections(queuedColumnSelections);
    dispatch({ type: "SET_QUEUED_COLUMN_SELECTIONS", payload: [] });
  };
  const setIsSelectingColumns = (isSelecting) => {
    dispatch({ type: "SET_IS_SELECTING_COLUMNS", payload: isSelecting });
  };
  const setSelectedColumnsState = (state2) => {
    dispatch({ type: "SET_SELECTED_COLUMNS_STATE", payload: state2 });
  };
  const setColumnSelections = (values) => {
    dispatch({ type: "SET_COLUMN_SELECTIONS", payload: values });
  };
  return {
    columnSelections,
    isSelectingColumns,
    setIsSelectingColumns,
    queuedColumnSelections,
    applyQueuedColumnSelections,
    addQueuedColumnSelections,
    setUniqueColumnSelections,
    clearColumnSelections,
    setSelectedColumnsState,
    selectedColumnsState,
    focusedColumn,
    setFocusedColumn,
    setColumnSelections
  };
};
const [ColumnSelectionProvider, useColumnSelectionContext] = constate(useColumnSelection);
export {
  ColumnSelectionProvider,
  useColumnSelection,
  useColumnSelectionContext
};
